import { createContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material/styles";
// color design tokens export

// New color palette based on modern construction/architecture theme
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#ffffff",
          200: "#f8f9fa",
          300: "#e9ecef",
          400: "#dee2e6",
          500: "#adb5bd",
          600: "#6c757d",
          700: "#495057",
          800: "#343a40",
          900: "#212529"
        },
        primary: {
          100: "#e3f2fd",
          200: "#bbdefb",
          300: "#90caf9",
          400: "#64b5f6",
          500: "#42a5f5",
          600: "#2196f3",
          700: "#1e88e5",
          800: "#1976d2",
          900: "#1565c0"
        },
        secondary: {
          100: "#fef6e7",
          200: "#fdecd0",
          300: "#fbe3b9",
          400: "#fad9a1",
          500: "#f9d08a",
          600: "#f8c673",
          700: "#f7bc5c",
          800: "#f6b344",
          900: "#f5a92d"
        },
        background: {
          100: "#1a1f2e",
          200: "#232936",
          300: "#2b323f",
          400: "#343b47",
          500: "#3c4450",
          600: "#454d59",
          700: "#4d5661",
          800: "#565f6a",
          900: "#5e6873"
        },
        success: {
          100: "#e8f5e9",
          200: "#c8e6c9",
          300: "#a5d6a7",
          400: "#81c784",
          500: "#66bb6a",
          600: "#4caf50",
          700: "#43a047",
          800: "#388e3c",
          900: "#2e7d32"
        },
        error: {
          100: "#ffebee",
          200: "#ffcdd2",
          300: "#ef9a9a",
          400: "#e57373",
          500: "#ef5350",
          600: "#f44336",
          700: "#e53935",
          800: "#d32f2f",
          900: "#c62828"
        }
      }
    : {
        grey: {
          100: "#212529",
          200: "#343a40",
          300: "#495057",
          400: "#6c757d",
          500: "#adb5bd",
          600: "#dee2e6",
          700: "#e9ecef",
          800: "#f8f9fa",
          900: "#ffffff"
        },
        primary: {
          100: "#1565c0",
          200: "#1976d2",
          300: "#1e88e5",
          400: "#2196f3",
          500: "#42a5f5",
          600: "#64b5f6",
          700: "#90caf9",
          800: "#bbdefb",
          900: "#e3f2fd"
        },
        secondary: {
          100: "#f5a92d",
          200: "#f6b344",
          300: "#f7bc5c",
          400: "#f8c673",
          500: "#f9d08a",
          600: "#fad9a1",
          700: "#fbe3b9",
          800: "#fdecd0",
          900: "#fef6e7"
        },
        background: {
          100: "#ffffff",
          200: "#f8f9fa",
          300: "#f1f3f5",
          400: "#e9ecef",
          500: "#dee2e6",
          600: "#ced4da",
          700: "#adb5bd",
          800: "#6c757d",
          900: "#495057"
        },
        success: {
          100: "#2e7d32",
          200: "#388e3c",
          300: "#43a047",
          400: "#4caf50",
          500: "#66bb6a",
          600: "#81c784",
          700: "#a5d6a7",
          800: "#c8e6c9",
          900: "#e8f5e9"
        },
        error: {
          100: "#c62828",
          200: "#d32f2f",
          300: "#e53935",
          400: "#f44336",
          500: "#ef5350",
          600: "#e57373",
          700: "#ef9a9a",
          800: "#ffcdd2",
          900: "#ffebee"
        }
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.primary[600],
              light: colors.primary[400],
              dark: colors.primary[800],
            },
            secondary: {
              main: colors.secondary[600],
              light: colors.secondary[400],
              dark: colors.secondary[800],
            },
            background: {
              default: colors.background[200],
              paper: colors.background[300],
            },
            text: {
              primary: colors.grey[100],
              secondary: colors.grey[300],
              disabled: colors.grey[500],
            },
            error: {
              main: colors.error[600],
              light: colors.error[400],
              dark: colors.error[800],
            },
            success: {
              main: colors.success[600],
              light: colors.success[400],
              dark: colors.success[800],
            },
            divider: colors.grey[700],
          }
        : {
            // The light-mode ramps run in the opposite direction to the dark ones: here a
            // HIGHER index is a LIGHTER colour. So `light` takes the index above `main` and
            // `dark` the index below it — the reverse of the mapping in the branch above.
            primary: {
              main: colors.primary[300],
              light: colors.primary[500],
              dark: colors.primary[100],
            },
            secondary: {
              main: colors.secondary[400],
              light: colors.secondary[600],
              dark: colors.secondary[200],
            },
            background: {
              default: colors.background[200],
              paper: colors.background[100],
            },
            text: {
              primary: colors.grey[100],
              secondary: colors.grey[300],
              disabled: colors.grey[500],
            },
            error: {
              main: colors.error[400],
              light: colors.error[600],
              dark: colors.error[200],
            },
            success: {
              main: colors.success[400],
              light: colors.success[600],
              dark: colors.success[200],
            },
            divider: colors.grey[300],
          }),
    },
    typography: {
      fontFamily: ["Source Sans 3", "sans-serif"].join(","),
      fontSize: 16,
      h1: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 40,
        fontWeight: 600,
      },
      h2: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 32,
        fontWeight: 600,
      },
      h3: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 24,
        fontWeight: 600,
      },
      h4: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 20,
        fontWeight: 600,
      },
      h5: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 16,
        fontWeight: 600,
      },
      h6: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 14,
        fontWeight: 600,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'dark' 
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

const COLOR_MODE_STORAGE_KEY = "av-facades-color-mode";

// Storage access throws outright when the browser blocks it (Safari private browsing,
// cookies disabled), so reads and writes are both guarded. A failure only means the
// preference will not stick — it must never stop the site rendering.
const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
};

const storeMode = (mode) => {
  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  } catch {
    // Preference simply will not persist for this visitor.
  }
};

export const useMode = () => {
  // Storage holds an explicit choice only, so it wins outright. Everyone else gets dark:
  // the site is designed dark-first, so that is the intended first impression regardless
  // of what the visitor's OS prefers.
  const [mode, setMode] = useState(() => readStoredMode() ?? "dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => {
          const next = prev === "light" ? "dark" : "light";
          storeMode(next);
          return next;
        }),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  // Keeps the pre-paint layer in public/index.html in step with the live theme: the data
  // attribute selects which of its two body rules applies, and theme-color drives the
  // mobile address bar, which would otherwise sit on the old colour until the next
  // reload. Read off the theme so these stay derived rather than a third copy of the hexes.
  useEffect(() => {
    document.documentElement.setAttribute("data-color-mode", mode);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme.palette.background.default);
  }, [mode, theme]);

  return [theme, colorMode];
};